import React, { useState, useEffect } from 'react';
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Badge,
  Fab,
} from '@mui/material';
import { MdPeople, MdPublic, MdDescription, MdMenu, MdChevronLeft, MdSettings } from 'react-icons/md';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisasScreen from '../screens/VisasScreen';
import DemandesScreen from '../screens/DemandesScreen';
import VoyageursScreen from '../screens/VoyageursScreen';
import MesVisasScreen from '../screens/MesVisasScreen';
import ParameterScreen from '../screens/ParameterScreen';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { recupererListeDemandes } from '../Services';
import { LANGBAR_HEIGHT } from './BTLangBar';

const SIDEBAR_WIDTH_EXPANDED = 240;
const SIDEBAR_WIDTH_COLLAPSED = 72;

interface CarouselItem {
  url: string;
}

interface CarouselProps {
  carouselItems: CarouselItem[];
}

const Carousel: React.FC<CarouselProps> = ({ carouselItems }) => {
  const [index, setIndex] = useState<number>(0);

  const next = () => {
    setIndex((prev) => (prev + 1) % carouselItems.length);
  };

  const prev = () => {
    setIndex((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <IconButton onClick={prev} sx={{ position: 'absolute', left: 0 }}>
        <ArrowBackIcon />
      </IconButton>
      <img
        src={carouselItems[index].url}
        alt={`Slide ${index}`}
        style={{ maxWidth: '100%', maxHeight: '80vh' }}
      />
      <IconButton onClick={next} sx={{ position: 'absolute', right: 0 }}>
        <ArrowForwardIcon />
      </IconButton>
    </Box>
  );
};

const asyncLocalStorage = {
  getItem: async (key: string) => {
    return Promise.resolve(localStorage.getItem(key));
  },
  setItem: async (key: string, value: string) => {
    localStorage.setItem(key, value);
    return Promise.resolve();
  },
  removeItem: async (key: string) => {
    localStorage.removeItem(key);
    return Promise.resolve();
  },
};

const navItems: Array<{ labelKey: string; icon: React.ReactNode }> = [
  { labelKey: 'tabs.destinations', icon: <MdPublic size={24} /> },
  { labelKey: 'tabs.voyageurs', icon: <MdPeople size={24} /> },
  { labelKey: 'tabs.demandes', icon: <MdDescription size={24} /> },
  {
    labelKey: 'tabs.mesVisas',
    icon: (
      <img
        src="/passport.png"
        alt=""
        style={{ width: 24, height: 24 }}
      />
    ),
  },
];

const MyTabs: React.FC = () => {
  const { t } = useTranslation('nav');
  const { t: tCommon } = useTranslation('common');
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isParameterOpen, setIsParameterOpen] = useState<boolean>(false);
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(true);
  const [demandesBadgeCount, setDemandesBadgeCount] = useState<number>(0);
  const location = useLocation();
  const [tabValue, setTabValue] = useState<number>(location.state?.tabValue || 0);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const sidebarWidth = sidebarExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

  const carouselItems: CarouselItem[] = [
    { url: 'https://betravel-app.visamonde.com/tuto/1.png' },
    { url: 'https://betravel-app.visamonde.com/tuto/2.png' },
    { url: 'https://betravel-app.visamonde.com/tuto/3.png' },
    { url: 'https://betravel-app.visamonde.com/tuto/4.png' },
    { url: 'https://betravel-app.visamonde.com/tuto/5.png' },
  ];

  useEffect(() => {
    if (location.state && typeof location.state.tabValue === 'number') {
      setTabValue(location.state.tabValue);
    }
  }, [location.state]);

  // Fetch pending demandes count for badge
  useEffect(() => {
    const fetchBadgeCount = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const demandes = await recupererListeDemandes(user.id_utilisateur);
          const pendingCount = demandes.filter(
            (d: any) =>
              d.etat_visa === 'Formulaire complet' ||
              d.etat_paiement === 'Attente facturation' ||
              d.etat_visa === 'Attente paiement'
          ).length;
          setDemandesBadgeCount(pendingCount);
        }
      } catch (e) {
        // silently fail
      }
    };
    fetchBadgeCount();
    const interval = setInterval(fetchBadgeCount, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const checkIfModalShown = async () => {
      try {
        const hasShownModal = await asyncLocalStorage.getItem('hasShownModal');
        if (!hasShownModal) {
          setIsModalVisible(true);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du modal :', error);
      }
    };

    checkIfModalShown();
    incrementLoginCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleModalClose = async () => {
    try {
      await asyncLocalStorage.setItem('hasShownModal', 'true');
      setIsModalVisible(false);
    } catch (error) {
      console.error('Erreur lors de la fermeture du modal :', error);
    }
  };

  const incrementLoginCount = async () => {
    try {
      const loginCountStr = await asyncLocalStorage.getItem('loginCount');
      const loginCount = loginCountStr ? parseInt(loginCountStr, 10) : 0;
      const newCount = loginCount + 1;
      await asyncLocalStorage.setItem('loginCount', newCount.toString());

      if (newCount >= 5) {
        await asyncLocalStorage.setItem('loginCount', '0');
      }
    } catch (error) {
      console.error("Erreur lors de l'incrémentation du compteur de connexion :", error);
    }
  };

  let ScreenComponent: React.FC;
  switch (tabValue) {
    case 0:
      ScreenComponent = VisasScreen;
      break;
    case 1:
      ScreenComponent = VoyageursScreen;
      break;
    case 2:
      ScreenComponent = DemandesScreen;
      break;
    case 3:
      ScreenComponent = MesVisasScreen;
      break;
    default:
      ScreenComponent = VisasScreen;
      break;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* ===== DESKTOP: Sidebar Navigation ===== */}
      {isDesktop && (
        <Drawer
          variant="permanent"
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            transition: 'width 0.3s ease',
            '& .MuiDrawer-paper': {
              width: sidebarWidth,
              boxSizing: 'border-box',
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
              backgroundColor: '#ffffff',
              borderRight: 'none',
              boxShadow: '2px 0 12px rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column',
              top: `${LANGBAR_HEIGHT}px`,
              height: `calc(100vh - ${LANGBAR_HEIGHT}px)`,
            },
          }}
        >
          {/* Logo + Toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: sidebarExpanded ? 'space-between' : 'center',
              px: sidebarExpanded ? 2 : 0,
              py: 2,
              minHeight: 80,
            }}
          >
            {sidebarExpanded && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
              >
                <Box
                  component="img"
                  src="/logo-betraveling.svg"
                  alt="BeTravel"
                  sx={{
                    width: 44,
                    height: 44,
                    objectFit: 'contain',
                    borderRadius: 1,
                    boxShadow: '0 4px 12px rgba(216, 157, 47, 0.30)',
                  }}
                />
                <Box
                  sx={{
                    fontFamily: "'Fraunces', serif",
                    fontSize: 22,
                    fontWeight: 600,
                    color: '#1A1A1A',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Be<Box component="span" sx={{ color: '#D89D2F' }}>Travel</Box>
                </Box>
              </Box>
            )}
            <IconButton
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              sx={{
                color: '#666',
                '&:hover': { backgroundColor: '#f0f0f0' },
              }}
            >
              {sidebarExpanded ? <MdChevronLeft size={24} /> : <MdMenu size={24} />}
            </IconButton>
          </Box>

          <Divider sx={{ mx: 1, opacity: 0.5 }} />

          {/* Nav Items */}
          <List sx={{ flex: 1, pt: 2, px: 1 }}>
            {navItems.map((item, index) => {
              const isActive = tabValue === index;
              return (
                <Tooltip
                  key={index}
                  title={sidebarExpanded ? '' : t(item.labelKey)}
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    onClick={() => setTabValue(index)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      mx: 0,
                      minHeight: 48,
                      justifyContent: sidebarExpanded ? 'flex-start' : 'center',
                      px: sidebarExpanded ? 2 : 0,
                      position: 'relative',
                      backgroundColor: isActive ? 'rgba(46, 125, 50, 0.08)' : 'transparent',
                      '&:hover': {
                        backgroundColor: isActive ? 'rgba(46, 125, 50, 0.12)' : '#f5f5f5',
                      },
                      // Active indicator bar
                      '&::before': isActive
                        ? {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 8,
                            bottom: 8,
                            width: 4,
                            borderRadius: '0 4px 4px 0',
                            backgroundColor: '#2e7d32',
                          }
                        : {},
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: sidebarExpanded ? 40 : 'unset',
                        justifyContent: 'center',
                        color: isActive ? '#2e7d32' : '#666',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      {index === 2 && demandesBadgeCount > 0 ? (
                        <Badge badgeContent={demandesBadgeCount} color="error" max={99}>
                          {item.icon}
                        </Badge>
                      ) : item.icon}
                    </ListItemIcon>
                    {sidebarExpanded && (
                      <ListItemText
                        primary={t(item.labelKey)}
                        primaryTypographyProps={{
                          fontSize: '0.95rem',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? '#2e7d32' : '#333',
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>

          <Divider sx={{ mx: 1, opacity: 0.5 }} />

          {/* Settings button at bottom */}
          <List sx={{ px: 1, pb: 1 }}>
            <Tooltip
              title={sidebarExpanded ? '' : t('settings')}
              placement="right"
              arrow
            >
              <ListItemButton
                onClick={() => setIsParameterOpen(true)}
                sx={{
                  borderRadius: 2,
                  justifyContent: sidebarExpanded ? 'flex-start' : 'center',
                  px: sidebarExpanded ? 2 : 0,
                  minHeight: 48,
                  '&:hover': { backgroundColor: '#f5f5f5' },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: sidebarExpanded ? 40 : 'unset',
                    justifyContent: 'center',
                    color: '#666',
                  }}
                >
                  <MdSettings size={24} />
                </ListItemIcon>
                {sidebarExpanded && (
                  <ListItemText
                    primary={t('settings')}
                    primaryTypographyProps={{
                      fontSize: '0.95rem',
                      color: '#333',
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </List>
        </Drawer>
      )}

      {/* ===== MAIN CONTENT ===== */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          // On mobile, add bottom padding for the nav bar
          pb: isDesktop ? 0 : '80px',
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <ScreenComponent />
        </Box>
      </Box>

      {/* ===== MOBILE: Bottom Navigation ===== */}
      {!isDesktop && (
        <BottomNavigation
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          showLabels
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: 64,
            zIndex: theme.zIndex.appBar,
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderTop: '1px solid rgba(0,0,0,0.06)',
            borderRadius: '16px 16px 0 0',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.06)',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              py: 1,
              transition: 'all 0.2s ease',
              color: '#999',
              '&.Mui-selected': {
                color: '#2e7d32',
              },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              '&.Mui-selected': {
                fontSize: '0.72rem',
                fontWeight: 600,
              },
            },
          }}
        >
          {navItems.map((item, index) => (
            <BottomNavigationAction
              key={index}
              label={t(item.labelKey)}
              icon={
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    transition: 'transform 0.2s ease',
                    transform: tabValue === index ? 'scale(1.1)' : 'scale(1)',
                  }}
                >
                  {index === 2 && demandesBadgeCount > 0 ? (
                    <Badge badgeContent={demandesBadgeCount} color="error" max={99}>
                      {item.icon}
                    </Badge>
                  ) : item.icon}
                  {/* Active indicator dot */}
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: '#2e7d32',
                      mt: 0.3,
                      opacity: tabValue === index ? 1 : 0,
                      transition: 'opacity 0.2s ease',
                    }}
                  />
                </Box>
              }
            />
          ))}
        </BottomNavigation>
      )}

      {/* Modal tutoriel */}
      <Dialog fullScreen={fullScreen} open={isModalVisible} onClose={handleModalClose}>
        <DialogTitle>{t('tutorial.title')}</DialogTitle>
        <DialogContent>
          <Carousel carouselItems={carouselItems} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose} color="primary">
            {tCommon('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Parameter Screen (desktop) */}
      <ParameterScreen open={isParameterOpen} onClose={() => setIsParameterOpen(false)} />

      {/* Floating Help Button */}
      <Fab
        size="medium"
        onClick={() => window.open('https://betravel-app.visamonde.com/#contact', '_blank')}
        sx={{
          position: 'fixed',
          bottom: isDesktop ? 24 : 80,
          right: 16,
          zIndex: 900,
          backgroundColor: '#2e7d32',
          color: 'white',
          boxShadow: '0 4px 14px rgba(46,125,50,0.4)',
          '&:hover': { backgroundColor: '#B5811F' },
        }}
      >
        <HelpOutlineIcon />
      </Fab>
    </Box>
  );
};

export default MyTabs;
